import * as React from "react";
import { ulid } from "ulid";
import { useParams } from "react-router";

import { PromptGeneratorContextProvider } from "@/components/feature/promptGenerator/contextProvider";
import { LeftSidebar } from "@/components/chat/LeftSidebar";
import { QAHistoryPanel } from "@/components/chat/QAHistoryPanel";
import { ArtefactsViewer } from "@/components/feature/artefactsViewer";
import { useGetFileDetailsQuery } from "@/api/files";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { findFirstFileId } from "@/modules/chat/chat.utils";
import { ChatSidePanel } from "@/modules/chat/components/ChatSidePanel";
import { CreatePromptLibraryItemDialog } from "@/modules/chat/components/CreatePromptLibraryItemDialog";

interface Props {
  children: React.ReactNode;
}

const ChatLayoutContent: React.FC<Props> = ({ children }) => {
  const { systemId = "", conversationId } = useParams<{ systemId: string; conversationId: string }>();
  const [promptGeneratorConversationId] = React.useState(() => ulid());
  const [isLeftPanelOpen, setIsLeftPanelOpen] = React.useState(true);

  const { setRootItem, setCurrentPdfId } = useCurrentPdf();

  const { data } = useGetFileDetailsQuery({ fse_id: systemId, depth: 200 });

  React.useEffect(() => {
    if (data) {
      setRootItem(data);
      if (data.type === "file") {
        setCurrentPdfId(data.id);
      } else {
        const firstFileId = findFirstFileId(data);
        if (firstFileId) {
          setCurrentPdfId(firstFileId);
        }
      }
    }
  }, [data, setRootItem, setCurrentPdfId]);

  return (
    <PromptGeneratorContextProvider>
      <div className="-mx-4 -my-8 flex h-screen overflow-hidden sm:-mx-6 lg:-mx-8">
        <LeftSidebar systemId={systemId} isOpen={isLeftPanelOpen} onToggle={() => setIsLeftPanelOpen(prev => !prev)} />

        <div className="flex flex-1 flex-col">
          <div className="mx-auto flex h-full w-[90%] max-w-[90%] flex-col pt-2" style={{ maxWidth: 800 }}>
            {children}
          </div>
        </div>

        <ChatSidePanel
          promptGeneratorConversationId={promptGeneratorConversationId}
          historyPanel={<QAHistoryPanel systemId={systemId} currentConversationId={conversationId} />}
          artefactsPanel={<ArtefactsViewer />}
        />
      </div>

      <CreatePromptLibraryItemDialog />
    </PromptGeneratorContextProvider>
  );
};

const ChatLayout = (props: Props) => (
  <CurrentPdfProvider>
    <ChatLayoutContent {...props} />
  </CurrentPdfProvider>
);
export default ChatLayout;
